import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Copy, Check, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Seo } from "../components/Seo";
import { AdminGate } from "../components/admin/AdminGate";
import { useAdminSession } from "../hooks/useAdminLeads";
import { SEO_VALUES, SEO_SECTIONS } from "../lib/seoChecklist";

const CopyValue = ({ item }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(item.value);
      setCopied(true);
      toast.success("Copied");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Couldn't access clipboard");
    }
  };
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3">
      <div className="min-w-0">
        <div className="text-xs uppercase tracking-wider text-slate-500">{item.label}</div>
        <code data-testid={`seo-value-${item.id}`} className="block truncate font-mono text-sm text-slate-900">{item.value}</code>
      </div>
      <button
        type="button"
        data-testid={`seo-copy-${item.id}`}
        onClick={copy}
        className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:border-cyan-500 transition-colors"
      >
        {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />} {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
};

const linkify = (text) =>
  text.split(/(https?:\/\/[^\s)]+)/g).map((part, i) =>
    /^https?:\/\//.test(part) ? (
      <a key={`${i}-${part}`} href={part} target="_blank" rel="noreferrer" className="text-cyan-700 underline underline-offset-2 inline-flex items-center gap-0.5">
        {part.replace(/^https?:\/\//, "")} <ExternalLink className="w-3 h-3" />
      </a>
    ) : (
      <span key={`${i}-${part.slice(0, 24)}`}>{part}</span>
    )
  );

const Section = ({ section }) => (
  <section data-testid={`seo-section-${section.id}`} className="rounded-2xl border border-slate-200 bg-white shadow-sm p-6 sm:p-8">
    <h2 className="text-xl font-bold text-slate-900">{section.title}</h2>
    {section.intro && <p className="mt-1 text-base text-slate-600">{section.intro}</p>}
    <ol className="mt-5 space-y-4">
      {section.steps.map((s, i) => (
        <li key={s.t} className="flex gap-4">
          <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-cyan-50 text-cyan-800 font-mono text-sm font-semibold border border-cyan-200">{i + 1}</span>
          <div>
            <div className="font-semibold text-slate-900">{s.t}</div>
            <p className="mt-0.5 text-base text-slate-700 leading-relaxed">{linkify(s.d)}</p>
          </div>
        </li>
      ))}
    </ol>
  </section>
);

export default function SeoChecklist() {
  const { authed, login } = useAdminSession();
  return (
    <AdminGate page="adminSeo" authed={authed} login={login}>
      <div className="min-h-screen pt-28 pb-20 px-4 sm:px-6 lg:px-8">
        <Seo page="adminSeo" />
        <div className="max-w-4xl mx-auto" data-testid="seo-checklist-page">
          <Link to="/admin" data-testid="seo-back-link" className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-cyan-700">
            <ArrowLeft className="w-4 h-4" /> Back to leads
          </Link>
          <h1 className="mt-4 text-3xl font-extrabold text-slate-900 tracking-tight">Search engine submission checklist</h1>
          <p className="mt-2 text-base text-slate-600">
            One-time setup for Google Search Console and Bing Webmaster Tools. Do it after the new build is live on lakemonitor.ai.
          </p>
          <div className="mt-8 grid gap-3" data-testid="seo-values">
            {SEO_VALUES.map((v) => <CopyValue key={v.id} item={v} />)}
          </div>
          <div className="mt-8 space-y-6">
            {SEO_SECTIONS.map((s) => <Section key={s.id} section={s} />)}
          </div>
        </div>
      </div>
    </AdminGate>
  );
}
